import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, type FormListFieldData, type TableColumnsType } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import InputNumber from "../../../../../../components/Input/InputNumber.tsx"
import { formListAddFocus } from "../../../../../../utils/form/form-list-add-focus.ts"
import { validateNumber } from "../../../../../../utils/validation/number.ts"
import type {
  TemplateDocumentFormData,
  TemplateDocumentScopeSectionPhase
} from "../../../../types/template-document-form-data.tsx"
import { DurationFormItem } from "../../DurationFormItem.tsx"
import { TextInput } from "../../../../../../components/Input/TextInput.tsx"

type FieldType = TemplateDocumentFormData["phases"]

interface GetColumnsArg {
  form: FormInstance<TemplateDocumentFormData>
  hiddenColumns: string[]
  disabled?: boolean
  onRemove: (name: number) => void
}

export const getDocumentScopeSectionColumnsColumns = ({
  form,
  hiddenColumns,
  disabled,
  onRemove
}: GetColumnsArg): TableColumnsType<FormListFieldData> => [
  {
    hidden: hiddenColumns?.includes("name"),
    title: "Scope Of Work",
    dataIndex: "name",
    key: "name",
    render: (_, field) => (
      <div className="space-y-2">
        <Form.Item<FieldType>
          name={[field.name, "name"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Name is required" }]}
        >
          <TextInput disabled={disabled} placeholder="Enter name" />
        </Form.Item>

        <Form.List name={[field.name, "checklist_items"]}>
          {(checklistFields, { add: addItem, remove: removeItem }) => (
            <div className="space-y-1">
              {checklistFields.map((checklistField) => (
                <div
                  key={checklistField.key}
                  className="flex gap-1 items-center"
                >
                  <span>•</span>
                  <Form.Item<
                    TemplateDocumentScopeSectionPhase["checklist_items"]
                  >
                    name={[checklistField.name]}
                    style={{ margin: 0, flex: 1 }}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Name is required"
                      }
                    ]}
                  >
                    <TextInput
                      disabled={disabled}
                      placeholder="Checklist item"
                      size="small"
                    />
                  </Form.Item>
                  <Button
                    disabled={disabled}
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeItem(checklistField.name)}
                  />
                </div>
              ))}
              <Button
                disabled={disabled}
                type="dashed"
                size="small"
                onClick={() => {
                  formListAddFocus<TemplateDocumentFormData>(
                    () => addItem(""),
                    checklistFields,
                    form,
                    ["phases", field.name, "checklist_items"]
                  )
                }}
                icon={<PlusOutlined />}
                className="w-full"
              >
                Item
              </Button>
            </div>
          )}
        </Form.List>
      </div>
    )
  },
  {
    hidden: hiddenColumns?.includes("duration"),
    title: "Duration",
    width: 255,
    dataIndex: "duration_days",
    key: "duration_days",
    render: (_, field) => (
      <DurationFormItem<FieldType>
        disabled={disabled}
        durationName={[field.name, "duration_days"]}
        typeName={[field.name, "duration_type"]}
        formItemProps={{ className: "mb-0" }}
      />
    )
  },
  {
    hidden: hiddenColumns?.includes("budget"),
    title: "Cost",
    width: 225,
    dataIndex: "budget",
    key: "budget",
    render: (_, field) => (
      <Form.Item<FieldType>
        name={[field.name, "budget"]}
        style={{ margin: 0 }}
        rules={[{ validator: validateNumber }]}
      >
        <InputNumber
          disabled={disabled}
          stringMode
          className={"w-40"}
          min={0}
          placeholder="0"
          max={99999999.99} // 8 digits before decimal
          precision={2}
          suffix={"$"}
        />
      </Form.Item>
    )
  },
  {
    title: "",
    width: 50,
    render: (_, field) => (
      <Button
        disabled={disabled}
        type="text"
        danger
        size={"large"}
        icon={<DeleteOutlined />}
        onClick={() => onRemove(field.name)}
      />
    )
  }
]
